import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';
import {
  Users,
  FileText,
  DollarSign,
  TrendingUp,
  Shield,
  Settings,
  Activity,
  AlertCircle,
  Loader2,
  Search,
  MoreVertical,
  Eye,
  Ban,
  Trash2,
  Star,
  StarOff,
  CheckCircle2,
  XCircle,
  Download,
  Bell,
  Clock,
  BarChart3,
  UserCheck,
  FileCheck,
  Megaphone,
  History,
  Filter,
  RefreshCw,
  ChevronRight,
  MessageCircle,
  Send,
  HeadphonesIcon,
  X,
  Calendar,
  Target,
  Wallet,
  PieChart,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const PlatformAdminDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState('');
  
  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  
  // Chat support states
  const [chatSessions, setChatSessions] = useState([]);
  const [chatStats, setChatStats] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [chatFilter, setChatFilter] = useState('escalated');
  
  // Filters
  const [userSearch, setUserSearch] = useState('');
  const [userStatus, setUserStatus] = useState('all');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignStatus, setCampaignStatus] = useState('all');
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Action loading
  const [actionLoading, setActionLoading] = useState({});

  // Detail modals
  const [showDonationsModal, setShowDonationsModal] = useState(false);
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [recentDonations, setRecentDonations] = useState([]);
  const [donationsLoading, setDonationsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState({});

  // Export function - handles authenticated CSV download
  const handleExport = async (type) => {
    setExportLoading(prev => ({ ...prev, [type]: true }));
    try {
      const response = await apiClient.get(`/platform/export/${type}?format=csv`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error(`Failed to export ${type}:`, err);
      alert(`Failed to export ${type}. Please try again.`);
    } finally {
      setExportLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login?redirect=platform-admin');
      return;
    }
    fetchData();
  }, [isAuthenticated, authLoading]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [analyticsRes, usersRes, campaignsRes, logsRes, settingsRes] = await Promise.all([
        apiClient.get('/platform/analytics/overview'),
        apiClient.get('/platform/users?limit=100'),
        apiClient.get('/platform/campaigns?limit=100'),
        apiClient.get('/platform/audit-logs?limit=50'),
        apiClient.get('/platform/settings'),
      ]);
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data.users || []);
      setCampaigns(campaignsRes.data.campaigns || []);
      setAuditLogs(logsRes.data.logs || []);
      setSettings(settingsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Access denied. Platform admin privileges required.');
      } else {
        setError('Failed to load platform data');
      }
      console.error('Platform data error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch recent donations for modal
  const fetchRecentDonations = async () => {
    setDonationsLoading(true);
    try {
      const response = await apiClient.get('/donations/recent?limit=50');
      // Handle both array response and object with donations property
      const donations = Array.isArray(response.data) ? response.data : (response.data.donations || []);
      setRecentDonations(donations);
    } catch (err) {
      console.error('Failed to fetch donations:', err);
      setRecentDonations([]);
    } finally {
      setDonationsLoading(false);
    }
  };

  // Open donations modal
  const openDonationsModal = () => {
    setShowDonationsModal(true);
    fetchRecentDonations();
  };

  // Open revenue modal
  const openRevenueModal = () => {
    setShowRevenueModal(true);
  };

  const handleUserAction = async (userId, action, reason = '') => {
    setActionLoading(prev => ({ ...prev, [`user-${userId}`]: true }));
    try {
      await apiClient.post(`/platform/users/${userId}/action`, { action, reason });
      fetchData();
    } catch (err) {
      console.error('User action error:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [`user-${userId}`]: false }));
    }
  };

  const handleCampaignAction = async (campaignId, action, reason = '') => {
    setActionLoading(prev => ({ ...prev, [`campaign-${campaignId}`]: true }));
    try {
      await apiClient.post(`/platform/campaigns/${campaignId}/action`, { action, reason });
      fetchData();
    } catch (err) {
      console.error('Campaign action error:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [`campaign-${campaignId}`]: false }));
    }
  };

  // Chat support functions
  const fetchChatSessions = async () => {
    try {
      const [sessionsRes, statsRes] = await Promise.all([
        apiClient.get(`/chat/admin/sessions${chatFilter !== 'all' ? `?status=${chatFilter}` : ''}`),
        apiClient.get('/chat/admin/stats')
      ]);
      setChatSessions(sessionsRes.data.sessions || []);
      setChatStats(statsRes.data);
    } catch (err) {
      console.error('Chat sessions error:', err);
    }
  };

  const handleSelectChat = async (session) => {
    setSelectedChat(session);
    try {
      const res = await apiClient.get(`/chat/admin/session/${session.session_id}`);
      setChatMessages(res.data.messages || []);
    } catch (err) {
      console.error('Chat messages error:', err);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedChat) return;
    try {
      await apiClient.post('/chat/admin/reply', {
        session_id: selectedChat.session_id,
        message: replyMessage,
        admin_id: user?.id,
        admin_name: user?.name || 'Support Team'
      });
      setReplyMessage('');
      // Refresh messages
      handleSelectChat(selectedChat);
    } catch (err) {
      console.error('Reply error:', err);
    }
  };

  const handleResolveChat = async (sessionId) => {
    try {
      await apiClient.post(`/chat/admin/resolve/${sessionId}`);
      fetchChatSessions();
      if (selectedChat?.session_id === sessionId) {
        handleSelectChat({ ...selectedChat, status: 'resolved' });
      }
    } catch (err) {
      console.error('Resolve error:', err);
    }
  };

  // Fetch chat sessions when tab changes to support
  useEffect(() => {
    if (activeTab === 'support') {
      fetchChatSessions();
      // Set up polling for new messages
      const interval = setInterval(fetchChatSessions, 10000);
      return () => clearInterval(interval);
    }
  }, [activeTab, chatFilter]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = !userSearch || 
      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearch.toLowerCase());
    const matchesStatus = userStatus === 'all' || 
      (userStatus === 'suspended' && u.suspended) ||
      (userStatus === 'verified' && u.kyc_status === 'approved') ||
      (userStatus === 'admin' && (u.role === 'admin' || u.role === 'super_admin'));
    return matchesSearch && matchesStatus;
  });

  const filteredCampaigns = campaigns.filter(c => {
    const matchesSearch = !campaignSearch || 
      c.title?.toLowerCase().includes(campaignSearch.toLowerCase());
    const matchesStatus = campaignStatus === 'all' || c.status === campaignStatus;
    return matchesSearch && matchesStatus;
  });

  // Select all campaigns
  const handleSelectAllCampaigns = () => {
    if (selectedCampaigns.length === filteredCampaigns.length) {
      setSelectedCampaigns([]);
    } else {
      setSelectedCampaigns(filteredCampaigns.map(c => c.id));
    }
  };

  // Toggle single campaign selection
  const handleToggleCampaignSelect = (campaignId) => {
    setSelectedCampaigns(prev => 
      prev.includes(campaignId) 
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  // Bulk delete selected campaigns
  const handleBulkDeleteCampaigns = async () => {
    if (selectedCampaigns.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedCampaigns.length} campaign(s)? This action cannot be undone.`)) {
      return;
    }
    
    setActionLoading(prev => ({ ...prev, bulkDelete: true }));
    try {
      // Delete each selected campaign
      await Promise.all(
        selectedCampaigns.map(id => 
          apiClient.post(`/platform/campaigns/${id}/action`, { action: 'delete' })
        )
      );
      
      // Remove deleted campaigns from state
      setCampaigns(prev => prev.filter(c => !selectedCampaigns.includes(c.id)));
      setSelectedCampaigns([]);
      
      alert(`Successfully deleted ${selectedCampaigns.length} campaign(s)`);
    } catch (err) {
      console.error('Bulk delete failed:', err);
      alert('Failed to delete some campaigns. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, bulkDelete: false }));
    }
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Bulk delete selected users
  const handleBulkDeleteUsers = async () => {
    if (selectedUsers.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} user(s)? This action cannot be undone.`)) {
      return;
    }
    
    setActionLoading(prev => ({ ...prev, bulkDeleteUsers: true }));
    try {
      // Delete each selected user
      await Promise.all(
        selectedUsers.map(id => 
          apiClient.post(`/platform/users/${id}/action`, { action: 'delete', reason: 'Bulk deleted by admin' })
        )
      );
      
      // Remove deleted users from state
      setUsers(prev => prev.filter(u => !selectedUsers.includes(u.id)));
      setSelectedUsers([]);
      
      alert(`Successfully deleted ${selectedUsers.length} user(s)`);
    } catch (err) {
      console.error('Bulk delete users failed:', err);
      alert('Failed to delete some users. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, bulkDeleteUsers: false }));
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full">
          <Alert className="bg-red-500/10 border-red-500/50">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <AlertDescription className="text-red-400">{error}</AlertDescription>
          </Alert>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                <Shield className="w-8 h-8 text-teal-400" />
                Platform Admin
              </h1>
              <p className="text-muted-foreground mt-1">SaaS Owner Dashboard - Full Platform Control</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchData} variant="outline" className="border-border">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="bg-teal-500 hover:bg-teal-600">
                    <Download className="w-4 h-4 mr-2" /> Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleExport('users')} disabled={exportLoading.users}>
                    {exportLoading.users ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                    Export Users (CSV)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('campaigns')} disabled={exportLoading.campaigns}>
                    {exportLoading.campaigns ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                    Export Campaigns (CSV)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('donations')} disabled={exportLoading.donations}>
                    {exportLoading.donations ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
                    Export Donations (CSV)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Stats Cards - Clickable */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card 
              className="bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border-emerald-500/30 cursor-pointer hover:border-emerald-400 transition-all"
              onClick={() => setActiveTab('users')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-400 text-sm font-medium">Total Users</p>
                    <p className="text-4xl font-bold text-foreground">{analytics?.users?.total || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      +{analytics?.users?.new_this_week || 0} this week
                    </p>
                  </div>
                  <Users className="w-10 h-10 text-emerald-400 opacity-50" />
                </div>
                <p className="text-xs text-emerald-400/70 mt-2">Click to manage users →</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 border-emerald-500/30 cursor-pointer hover:border-emerald-400 transition-all"
              onClick={openDonationsModal}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-400 text-sm font-medium">Total Raised</p>
                    <p className="text-4xl font-bold text-foreground">
                      {formatCurrency(analytics?.financials?.total_raised || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analytics?.financials?.total_donations || 0} donations
                    </p>
                  </div>
                  <DollarSign className="w-10 h-10 text-emerald-400 opacity-50" />
                </div>
                <p className="text-xs text-emerald-400/70 mt-2">Click to view donation details →</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500/30 cursor-pointer hover:border-teal-400 transition-all"
              onClick={openRevenueModal}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-teal-400 text-sm font-medium">Platform Revenue</p>
                    <p className="text-4xl font-bold text-foreground">
                      {formatCurrency(analytics?.financials?.platform_revenue || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">0% platform fee</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-teal-400 opacity-50" />
                </div>
                <p className="text-xs text-teal-400/70 mt-2">Click to view revenue breakdown →</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30 cursor-pointer hover:border-amber-400 transition-all"
              onClick={() => setActiveTab('campaigns')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-400 text-sm font-medium">Active Campaigns</p>
                    <p className="text-4xl font-bold text-foreground">{analytics?.campaigns?.active || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analytics?.campaigns?.pending || 0} pending review
                    </p>
                  </div>
                  <FileText className="w-10 h-10 text-amber-400 opacity-50" />
                </div>
                <p className="text-xs text-amber-400/70 mt-2">Click to manage campaigns →</p>
              </CardContent>
            </Card>
          </div>

          {/* Pending Actions Alert */}
          {(analytics?.health?.pending_kyc > 0 || analytics?.health?.pending_campaigns > 0) && (
            <Alert className="mb-6 bg-yellow-500/10 border-yellow-500/50">
              <Clock className="w-4 h-4 text-yellow-400" />
              <AlertDescription className="text-yellow-400">
                <strong>Action Required:</strong> {analytics?.health?.pending_kyc || 0} KYC reviews and {analytics?.health?.pending_campaigns || 0} campaign approvals pending.
              </AlertDescription>
            </Alert>
          )}

          {/* Main Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-card border border-border mb-6 flex-wrap h-auto p-1">
              <TabsTrigger value="overview" className="data-[state=active]:bg-teal-500">
                <BarChart3 className="w-4 h-4 mr-2" /> Overview
              </TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-teal-500">
                <Users className="w-4 h-4 mr-2" /> Users
                <Badge className="ml-2 bg-muted">{users.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="campaigns" className="data-[state=active]:bg-teal-500">
                <FileText className="w-4 h-4 mr-2" /> Campaigns
                <Badge className="ml-2 bg-muted">{campaigns.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="audit" className="data-[state=active]:bg-teal-500">
                <History className="w-4 h-4 mr-2" /> Audit Logs
              </TabsTrigger>
              <TabsTrigger value="support" className="data-[state=active]:bg-emerald-500">
                <HeadphonesIcon className="w-4 h-4 mr-2" /> Support Chat
                {chatStats?.escalated_sessions > 0 && (
                  <Badge className="ml-2 bg-red-500 animate-pulse">{chatStats.escalated_sessions}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-teal-500">
                <Settings className="w-4 h-4 mr-2" /> Settings
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Quick Stats */}
                <Card className="bg-card/50 border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Platform Health</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">User Verification Rate</span>
                      <span className="text-foreground font-bold">{analytics?.users?.verification_rate || 0}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Avg Donation</span>
                      <span className="text-foreground font-bold">{formatCurrency(analytics?.financials?.average_donation || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Featured Campaigns</span>
                      <span className="text-foreground font-bold">{analytics?.campaigns?.featured || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Pending KYC</span>
                      <Badge className="bg-yellow-500/20 text-yellow-400">{analytics?.health?.pending_kyc || 0}</Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card className="bg-card/50 border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Recent Admin Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {auditLogs.slice(0, 5).map((log) => (
                        <div key={log.id} className="flex items-start gap-3 p-2 bg-secondary/50 rounded-lg">
                          <Activity className="w-4 h-4 text-teal-400 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-foreground">
                              <span className="font-medium">{log.admin_name}</span> {log.action} {log.target_type}
                            </p>
                            <p className="text-xs text-muted-foreground">{formatDate(log.timestamp)}</p>
                          </div>
                        </div>
                      ))}
                      {auditLogs.length === 0 && (
                        <p className="text-muted-foreground text-center py-4">No recent actions</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <CardTitle className="text-foreground">User Management</CardTitle>
                    <div className="flex gap-2 flex-wrap">
                      {/* Bulk Actions */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (selectedUsers.length === filteredUsers.length) {
                            setSelectedUsers([]);
                          } else {
                            setSelectedUsers(filteredUsers.map(u => u.id));
                          }
                        }}
                        className="border-border"
                      >
                        {selectedUsers.length === filteredUsers.length && filteredUsers.length > 0 ? 'Deselect All' : 'Select All'}
                      </Button>
                      {selectedUsers.length > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleBulkDeleteUsers}
                          disabled={actionLoading.bulkDeleteUsers}
                        >
                          {actionLoading.bulkDeleteUsers ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-2" />
                          )}
                          Delete Selected ({selectedUsers.length})
                        </Button>
                      )}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search users..."
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          className="pl-9 w-64 bg-secondary border-border"
                        />
                      </div>
                      <Select value={userStatus} onValueChange={setUserStatus}>
                        <SelectTrigger className="w-40 bg-secondary border-border">
                          <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Users</SelectItem>
                          <SelectItem value="verified">Verified</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                          <SelectItem value="admin">Admins</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium w-10">
                            <input 
                              type="checkbox"
                              checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                              onChange={() => {
                                if (selectedUsers.length === filteredUsers.length) {
                                  setSelectedUsers([]);
                                } else {
                                  setSelectedUsers(filteredUsers.map(u => u.id));
                                }
                              }}
                              className="rounded border-border"
                            />
                          </th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">User</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Status</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Role</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Campaigns</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Joined</th>
                          <th className="text-right py-3 px-4 text-muted-foreground font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((u) => (
                          <tr key={u.id} className={`border-b border-border/50 hover:bg-secondary/30 ${selectedUsers.includes(u.id) ? 'bg-secondary/50' : ''}`}>
                            <td className="py-3 px-4">
                              <input 
                                type="checkbox"
                                checked={selectedUsers.includes(u.id)}
                                onChange={() => toggleUserSelection(u.id)}
                                className="rounded border-border"
                              />
                            </td>
                            <td className="py-3 px-4">
                              <div>
                                <p className="text-foreground font-medium">{u.name}</p>
                                <p className="text-sm text-muted-foreground">{u.email}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              {u.suspended ? (
                                <Badge className="bg-red-500/20 text-red-400">Suspended</Badge>
                              ) : u.kyc_status === 'approved' ? (
                                <Badge className="bg-emerald-500/20 text-emerald-400">Verified</Badge>
                              ) : (
                                <Badge className="bg-slate-500/20 text-muted-foreground">Basic</Badge>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={
                                u.role === 'super_admin' ? 'bg-teal-500/20 text-teal-400' :
                                u.role === 'admin' ? 'bg-emerald-500/20 text-emerald-400' :
                                'bg-slate-500/20 text-muted-foreground'
                              }>
                                {u.role || 'user'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-foreground">{u.campaign_count || 0}</td>
                            <td className="py-3 px-4 text-muted-foreground text-sm">{formatDate(u.created_at)}</td>
                            <td className="py-3 px-4 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => navigate(`/admin/users/${u.id}`)}>
                                    <Eye className="w-4 h-4 mr-2" /> View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  {u.suspended ? (
                                    <DropdownMenuItem onClick={() => handleUserAction(u.id, 'unsuspend')}>
                                      <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-400" /> Unsuspend
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => handleUserAction(u.id, 'suspend', 'Suspended by admin')}>
                                      <Ban className="w-4 h-4 mr-2 text-red-400" /> Suspend
                                    </DropdownMenuItem>
                                  )}
                                  {u.role !== 'admin' && u.role !== 'super_admin' ? (
                                    <DropdownMenuItem onClick={() => handleUserAction(u.id, 'make_admin')}>
                                      <Shield className="w-4 h-4 mr-2 text-emerald-400" /> Make Admin
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => handleUserAction(u.id, 'remove_admin')}>
                                      <Shield className="w-4 h-4 mr-2" /> Remove Admin
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleUserAction(u.id, 'delete', 'Deleted by admin')}
                                    className="text-red-400"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete User
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Campaigns Tab */}
            <TabsContent value="campaigns">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <CardTitle className="text-foreground">Campaign Management</CardTitle>
                    <div className="flex gap-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search campaigns..."
                          value={campaignSearch}
                          onChange={(e) => setCampaignSearch(e.target.value)}
                          className="pl-9 w-64 bg-secondary border-border"
                        />
                      </div>
                      <Select value={campaignStatus} onValueChange={setCampaignStatus}>
                        <SelectTrigger className="w-40 bg-secondary border-border">
                          <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="pending_verification">Pending</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* Select All & Bulk Actions */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAllCampaigns}
                        className="border-border"
                      >
                        {selectedCampaigns.length === filteredCampaigns.length && filteredCampaigns.length > 0
                          ? `Deselect All (${filteredCampaigns.length})`
                          : `Select All (${filteredCampaigns.length})`
                        }
                      </Button>
                      {selectedCampaigns.length > 0 && (
                        <span className="text-sm text-emerald-400">
                          {selectedCampaigns.length} selected
                        </span>
                      )}
                    </div>
                    {selectedCampaigns.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleBulkDeleteCampaigns}
                        disabled={actionLoading.bulkDelete}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {actionLoading.bulkDelete ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Delete Selected ({selectedCampaigns.length})
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {filteredCampaigns.map((c) => (
                      <div key={c.id} className="flex items-center gap-4 p-4 bg-secondary/30 rounded-xl">
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={selectedCampaigns.includes(c.id)}
                          onChange={() => handleToggleCampaignSelect(c.id)}
                          className="w-5 h-5 rounded border-border bg-secondary text-emerald-500 focus:ring-emerald-500 cursor-pointer"
                        />
                        <img
                          src={c.image || 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=100'}
                          alt={c.title}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-foreground font-medium truncate">{c.title}</h4>
                            {c.featured && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />}
                          </div>
                          <p className="text-sm text-muted-foreground">By {c.creator?.name}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-emerald-400">{formatCurrency(c.raised || c.raised_amount || c.current_amount || 0)} raised</span>
                            <span className="text-muted-foreground">of {formatCurrency(c.goal || c.goal_amount || 0)}</span>
                            <Badge className={
                              c.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                              c.status === 'pending_verification' ? 'bg-yellow-500/20 text-yellow-400' :
                              c.status === 'suspended' ? 'bg-red-500/20 text-red-400' :
                              'bg-slate-500/20 text-muted-foreground'
                            }>
                              {c.status}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`/campaign/${c.id}`, '_blank')}
                            className="border-border"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="border-border">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {c.status === 'pending_verification' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleCampaignAction(c.id, 'approve')}>
                                    <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-400" /> Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleCampaignAction(c.id, 'reject', 'Does not meet guidelines')}>
                                    <XCircle className="w-4 h-4 mr-2 text-red-400" /> Reject
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              {c.featured ? (
                                <DropdownMenuItem onClick={() => handleCampaignAction(c.id, 'unfeature')}>
                                  <StarOff className="w-4 h-4 mr-2" /> Remove Featured
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleCampaignAction(c.id, 'feature')}>
                                  <Star className="w-4 h-4 mr-2 text-yellow-400" /> Feature Campaign
                                </DropdownMenuItem>
                              )}
                              {c.status === 'suspended' ? (
                                <DropdownMenuItem onClick={() => handleCampaignAction(c.id, 'unsuspend')}>
                                  <CheckCircle2 className="w-4 h-4 mr-2" /> Unsuspend
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleCampaignAction(c.id, 'suspend', 'Suspended by admin')}>
                                  <Ban className="w-4 h-4 mr-2 text-red-400" /> Suspend
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleCampaignAction(c.id, 'delete')}
                                className="text-red-400"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Audit Logs Tab */}
            <TabsContent value="audit">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="text-foreground">Audit Logs</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Track all admin actions for compliance and security
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {auditLogs.map((log) => (
                      <div key={log.id} className="flex items-start gap-4 p-4 bg-secondary/30 rounded-lg">
                        <div className="p-2 bg-teal-500/20 rounded-lg">
                          <History className="w-5 h-5 text-teal-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-foreground font-medium">{log.admin_name}</span>
                            <Badge className="bg-muted">{log.action}</Badge>
                            <span className="text-muted-foreground">{log.target_type}</span>
                          </div>
                          {log.details?.reason && (
                            <p className="text-sm text-muted-foreground">Reason: {log.details.reason}</p>
                          )}
                          {log.details?.campaign_title && (
                            <p className="text-sm text-muted-foreground">Campaign: {log.details.campaign_title}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">{formatDate(log.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                    {auditLogs.length === 0 && (
                      <p className="text-muted-foreground text-center py-8">No audit logs yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Support Chat Tab */}
            <TabsContent value="support">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Chat Sessions List */}
                <div className="lg:col-span-1">
                  <Card className="bg-card/50 border-border">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">Chat Sessions</CardTitle>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={fetchChatSessions}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button 
                          size="sm" 
                          variant={chatFilter === 'escalated' ? 'default' : 'outline'}
                          onClick={() => setChatFilter('escalated')}
                          className={chatFilter === 'escalated' ? 'bg-red-500' : ''}
                        >
                          Escalated {chatStats?.escalated_sessions > 0 && `(${chatStats.escalated_sessions})`}
                        </Button>
                        <Button 
                          size="sm" 
                          variant={chatFilter === 'all' ? 'default' : 'outline'}
                          onClick={() => setChatFilter('all')}
                        >
                          All
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="max-h-[500px] overflow-y-auto">
                      <div className="space-y-2">
                        {chatSessions.map((session) => (
                          <div
                            key={session.session_id}
                            onClick={() => handleSelectChat(session)}
                            className={`p-3 rounded-lg cursor-pointer transition-colors ${
                              selectedChat?.session_id === session.session_id
                                ? 'bg-emerald-500/20 border border-emerald-500'
                                : 'bg-secondary/50 hover:bg-secondary'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm truncate">
                                {session.user_name || session.user_email || 'Anonymous'}
                              </span>
                              <Badge className={
                                session.status === 'escalated' ? 'bg-red-500' :
                                session.status === 'resolved' ? 'bg-green-500' : 'bg-slate-500'
                              }>
                                {session.status}
                              </Badge>
                            </div>
                            {session.user_email && (
                              <p className="text-xs text-muted-foreground truncate">{session.user_email}</p>
                            )}
                            <p className="text-xs text-muted-foreground truncate mt-1">{session.last_message}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-muted-foreground">
                                {session.message_count} messages
                              </span>
                              {session.unread_count > 0 && (
                                <Badge className="bg-emerald-500">{session.unread_count} new</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                        {chatSessions.length === 0 && (
                          <p className="text-muted-foreground text-center py-8">No chat sessions</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Chat Messages */}
                <div className="lg:col-span-2">
                  <Card className="bg-card/50 border-border h-[600px] flex flex-col">
                    {selectedChat ? (
                      <>
                        <CardHeader className="border-b border-border pb-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-lg flex items-center gap-2">
                                <HeadphonesIcon className="w-5 h-5 text-emerald-500" />
                                {selectedChat.user_name || 'Anonymous User'}
                              </CardTitle>
                              <p className="text-sm text-muted-foreground">{selectedChat.user_email || 'No email provided'}</p>
                            </div>
                            <div className="flex gap-2">
                              {selectedChat.status !== 'resolved' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleResolveChat(selectedChat.session_id)}
                                  className="text-green-400 border-green-400 hover:bg-green-400/10"
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="flex-1 overflow-y-auto p-4">
                          <div className="space-y-4">
                            {chatMessages.map((msg, index) => (
                              <div
                                key={index}
                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                              >
                                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                                  msg.role === 'user' 
                                    ? 'bg-emerald-500 text-white rounded-br-sm' 
                                    : msg.role === 'support'
                                    ? 'bg-emerald-500 text-white rounded-bl-sm'
                                    : 'bg-secondary text-foreground rounded-bl-sm'
                                }`}>
                                  {msg.role === 'support' && (
                                    <p className="text-xs opacity-80 mb-1">Support Team</p>
                                  )}
                                  {msg.role === 'assistant' && (
                                    <p className="text-xs opacity-80 mb-1">AI Assistant</p>
                                  )}
                                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                  <p className="text-xs opacity-60 mt-1">
                                    {new Date(msg.created_at).toLocaleTimeString()}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                        {selectedChat.status !== 'resolved' && (
                          <div className="p-4 border-t border-border">
                            <div className="flex gap-2">
                              <Input
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                placeholder="Type your reply..."
                                className="flex-1 bg-secondary border-border"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendReply();
                                  }
                                }}
                              />
                              <Button 
                                onClick={handleSendReply}
                                disabled={!replyMessage.trim()}
                                className="bg-emerald-500 hover:bg-emerald-600"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-muted-foreground">
                        <div className="text-center">
                          <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>Select a chat session to view messages</p>
                        </div>
                      </div>
                    )}
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="text-foreground">Platform Settings</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Configure platform-wide settings and policies
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="text-foreground font-medium">Financial Settings</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                          <span className="text-muted-foreground">Platform Fee</span>
                          <span className="text-foreground font-medium">{settings?.platform_fee_percentage || 5}%</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                          <span className="text-muted-foreground">Min Campaign Goal</span>
                          <span className="text-foreground font-medium">{formatCurrency(settings?.min_campaign_goal || 100)}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                          <span className="text-muted-foreground">Max Campaign Goal</span>
                          <span className="text-foreground font-medium">{formatCurrency(settings?.max_campaign_goal || 10000000)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h4 className="text-foreground font-medium">Platform Policies</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                          <span className="text-muted-foreground">Auto-approve Campaigns</span>
                          <Badge className={settings?.auto_approve_campaigns ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted'}>
                            {settings?.auto_approve_campaigns ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                          <span className="text-muted-foreground">Require KYC for Withdrawal</span>
                          <Badge className={settings?.require_kyc_for_withdrawal ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted'}>
                            {settings?.require_kyc_for_withdrawal !== false ? 'Required' : 'Optional'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                          <span className="text-muted-foreground">Maintenance Mode</span>
                          <Badge className={settings?.maintenance_mode ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}>
                            {settings?.maintenance_mode ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      {/* Donations/Transactions Detail Modal */}
      <Dialog open={showDonationsModal} onOpenChange={setShowDonationsModal}>
        <DialogContent className="max-w-4xl bg-card border-border max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              Transaction Details
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Complete list of all donation transactions on the platform
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4 flex-1 overflow-hidden flex flex-col">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-lg p-3 text-center border border-emerald-500/30">
                <p className="text-emerald-400 text-xs font-medium">Total Raised</p>
                <p className="text-xl font-bold text-foreground">{formatCurrency(analytics?.financials?.total_raised || 0)}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-3 text-center">
                <p className="text-blue-400 text-xs font-medium">Total Transactions</p>
                <p className="text-xl font-bold text-foreground">{analytics?.financials?.total_donations || 0}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-3 text-center">
                <p className="text-amber-400 text-xs font-medium">Avg. Donation</p>
                <p className="text-xl font-bold text-foreground">
                  {formatCurrency(analytics?.financials?.total_donations > 0 
                    ? (analytics?.financials?.total_raised / analytics?.financials?.total_donations) 
                    : 0)}
                </p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-3 text-center">
                <p className="text-teal-400 text-xs font-medium">Platform Fee (0%)</p>
                <p className="text-xl font-bold text-foreground">
                  {formatCurrency(analytics?.financials?.platform_revenue || 0)}
                </p>
              </div>
            </div>

            {/* Transaction List */}
            <div className="bg-secondary/30 rounded-lg flex-1 overflow-hidden flex flex-col">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <h4 className="text-foreground font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  All Transactions ({recentDonations.length})
                </h4>
                <Badge className="bg-emerald-500/20 text-emerald-400">
                  {donationsLoading ? 'Loading...' : `${recentDonations.length} records`}
                </Badge>
              </div>
              
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-secondary/50 text-xs font-medium text-muted-foreground border-b border-border">
                <div className="col-span-1">#</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-2">Donor</div>
                <div className="col-span-3">Campaign</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-2 text-center">Status</div>
              </div>
              
              {/* Transaction Rows */}
              <div className="flex-1 overflow-y-auto">
                {donationsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
                  </div>
                ) : recentDonations.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No transactions found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-700">
                    {recentDonations.map((donation, idx) => (
                      <div key={donation.id || idx} className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-secondary/30 transition-colors items-center text-sm">
                        <div className="col-span-1 text-muted-foreground text-xs">{idx + 1}</div>
                        <div className="col-span-2">
                          <p className="text-foreground text-xs">{new Date(donation.created_at).toLocaleDateString()}</p>
                          <p className="text-muted-foreground text-xs">{new Date(donation.created_at).toLocaleTimeString()}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-foreground text-sm truncate">{donation.donor_name || 'Anonymous'}</p>
                          {donation.anonymous && <Badge className="bg-muted text-xs">Anonymous</Badge>}
                        </div>
                        <div className="col-span-3">
                          <p className="text-foreground/80 text-xs truncate">{donation.campaign_title}</p>
                        </div>
                        <div className="col-span-2 text-right">
                          <p className="text-emerald-400 font-semibold">{formatCurrency(donation.amount)}</p>
                        </div>
                        <div className="col-span-2 text-center">
                          <Badge className={
                            donation.status === 'completed' 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : donation.status === 'pending'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-red-500/20 text-red-400'
                          }>
                            {donation.status || 'completed'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-between items-center pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Showing {recentDonations.length} most recent transactions
              </p>
              <Button 
                variant="outline" 
                className="border-border text-foreground/80"
                onClick={() => handleExport('donations')}
                disabled={exportLoading.donations}
              >
                {exportLoading.donations ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export All Transactions
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revenue Detail Modal */}
      <Dialog open={showRevenueModal} onOpenChange={setShowRevenueModal}>
        <DialogContent className="max-w-2xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-teal-400" />
              Platform Revenue Breakdown
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Detailed view of platform earnings and fee structure
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Revenue Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-teal-500/20 to-emerald-500/20 rounded-lg p-4 text-center border border-teal-500/30">
                <p className="text-teal-400 text-sm">Total Platform Revenue</p>
                <p className="text-3xl font-bold text-foreground">{formatCurrency(analytics?.financials?.platform_revenue || 0)}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-emerald-400 text-sm">Total Amount Processed</p>
                <p className="text-3xl font-bold text-foreground">{formatCurrency(analytics?.financials?.total_raised || 0)}</p>
              </div>
            </div>

            {/* Fee Breakdown */}
            <div className="bg-secondary/30 rounded-lg p-4">
              <h4 className="text-foreground font-medium mb-4 flex items-center gap-2">
                <PieChart className="w-4 h-4 text-muted-foreground" />
                Fee Structure
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                    <span className="text-foreground/80">Platform Fee (0%)</span>
                  </div>
                  <span className="text-teal-400 font-medium">{formatCurrency(analytics?.financials?.platform_revenue || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <span className="text-foreground/80">Creator Earnings</span>
                  </div>
                  <span className="text-emerald-400 font-medium">
                    {formatCurrency((analytics?.financials?.total_raised || 0) - (analytics?.financials?.platform_revenue || 0))}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <span className="text-foreground/80">Tips Collected</span>
                  </div>
                  <span className="text-amber-400 font-medium">{formatCurrency(analytics?.financials?.tips_collected || 0)}</span>
                </div>
              </div>
            </div>

            {/* Revenue by Period */}
            <div className="bg-secondary/30 rounded-lg p-4">
              <h4 className="text-foreground font-medium mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                Revenue by Period
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center py-3 bg-secondary/50 rounded-lg">
                  <p className="text-muted-foreground text-xs">This Week</p>
                  <p className="text-teal-400 font-bold">
                    {formatCurrency((analytics?.financials?.raised_this_week || 0) * 0.05)}
                  </p>
                </div>
                <div className="text-center py-3 bg-secondary/50 rounded-lg">
                  <p className="text-muted-foreground text-xs">This Month</p>
                  <p className="text-teal-400 font-bold">
                    {formatCurrency((analytics?.financials?.raised_this_month || 0) * 0.05)}
                  </p>
                </div>
              </div>
            </div>

            {/* Settings Link */}
            <div className="flex justify-end">
              <Button 
                variant="outline" 
                className="border-border text-foreground/80"
                onClick={() => { setShowRevenueModal(false); setActiveTab('settings'); }}
              >
                <Settings className="w-4 h-4 mr-2" />
                Manage Fee Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default PlatformAdminDashboard;
